/**
 * ═══════════════════════════════════════════════════════════════
 *  SYSTEM DRAWER — Phase 3 sidebar grouping
 * ═══════════════════════════════════════════════════════════════
 *
 * Admin / config / governance routes that collapse into a single
 * "System" drawer at the bottom of the sidebar instead of cluttering
 * the main nav.
 *
 * Rules (Phase 3):
 *  - Routes stay live; ROLE_ALLOWED_ROUTES unchanged.
 *  - Items inside the drawer still respect role permissions.
 *  - Drawer is collapsed by default for non-admin roles, expanded
 *    by default for super_admin / owner / admin.
 *  - User can toggle open/closed (state persisted in localStorage).
 */

import type { UserRole } from "./RoleContext";

/**
 * Canonical list of routes that belong in the System drawer.
 * Source of truth — both AppLayout and CommandRailSidebar consume it.
 */
export const SYSTEM_DRAWER_ROUTES: readonly string[] = [
  "/settings",
  "/navigation-settings",
  "/control-plane",
  "/system-audit",
  "/architecture-health",
  "/automations",
  "/follow-up-engine",
  "/alerts",
  "/offerings",
  "/product-registry",
  "/demo-lab",
  "/friction-log",
  "/checklist-templates",
] as const;

const SYSTEM_ROUTE_SET = new Set<string>(SYSTEM_DRAWER_ROUTES);

/**
 * True if the route belongs in the System drawer (regardless of role).
 */
export function isSystemDrawerRoute(route: string): boolean {
  return SYSTEM_ROUTE_SET.has(route);
}

/**
 * Roles for which the drawer should default to OPEN.
 * All other roles see it collapsed on first load (they can still toggle).
 */
const DRAWER_DEFAULT_OPEN_ROLES: ReadonlySet<UserRole> = new Set<UserRole>([
  "super_admin",
  "owner",
  "admin",
]);

export function isDrawerOpenByDefault(role: UserRole): boolean {
  return DRAWER_DEFAULT_OPEN_ROLES.has(role);
}

const DRAWER_OPEN_KEY = "koredor.systemDrawer.open";

/**
 * Read user's persisted open/closed preference (null = use role default).
 */
export function getStoredDrawerOpen(): boolean | null {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(DRAWER_OPEN_KEY);
  if (v === "true") return true;
  if (v === "false") return false;
  return null;
}

export function setStoredDrawerOpen(open: boolean): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(DRAWER_OPEN_KEY, String(open));
}
